import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { useRouter } from 'next/router';

import { selectCurrentUser } from 'features/auth/slice';
import { selectIsTalentProfileComplete, signOut } from 'features/auth/slice';
import { useDispatch, useSelector } from 'react-redux';

import { flash, Icon, Menu, NavLink } from '@frontendmentorio/design-system-v2';

import Avatar from 'features/shared/Avatar';
import MessagesNavLink from 'features/pages/Messaging/NavLink';
import NotificationsNavLink from 'features/notifications/NavLink';
import TalentProfileNavLink from 'features/pages/TalentProfile/NavLink';

const iconStyles = tv({
  base: ['text-neutral-900', 'transition-transform'],
  variants: {
    isActive: {
      true: ['rotate-180'],
    },
  },
});

const MenuButton = forwardRef(({ isActive, ...props }, ref) => {
  const currentUser = useSelector(selectCurrentUser);

  return (
    <button
      {...props}
      aria-label="Profile menu"
      className="flex gap-2 items-center"
      ref={ref}
    >
      <Avatar size="sm" user={currentUser} />

      <Icon
        className={iconStyles({ isActive })}
        name="chevron-down"
        style="solid"
      />
    </button>
  );
});

export default function SignedInLinks() {
  const dispatch = useDispatch();
  const router = useRouter();

  const canViewMessages = useSelector(selectIsTalentProfileComplete);
  const currentUser = useSelector(selectCurrentUser);

  const handleSignOut = async () => {
    await dispatch(signOut());

    flash.success('You have successfully signed out. See you again soon!');

    router.push('/');
  };

  return (
    <div className="flex gap-4 items-center">
      <ul className="flex gap-4 items-center">
        <li>
          <NotificationsNavLink />
        </li>

        <li>
          <TalentProfileNavLink currentUser={currentUser} />
        </li>

        {canViewMessages && (
          <li>
            <MessagesNavLink />
          </li>
        )}
      </ul>

      <Menu className="flex" button={MenuButton}>
        <Menu.Nav>
          <Menu.Item>
            <NavLink href="/" isUnderlineOnHover={false}>
              <Icon className="text-lg" name="house" />
              Home
            </NavLink>
          </Menu.Item>

          <Menu.Item>
            <NavLink
              href={`/profile/${currentUser.username}`}
              isUnderlineOnHover={false}
            >
              <Icon className="text-lg" name="user" />
              Profile
            </NavLink>
          </Menu.Item>

          <Menu.Item>
            <NavLink href="/settings" isUnderlineOnHover={false}>
              <Icon className="text-lg" name="gear" />
              Settings
            </NavLink>
          </Menu.Item>

          <Menu.Item>
            <NavLink
              className="text-rose-600"
              isUnderlineOnHover={false}
              onClick={handleSignOut}
            >
              <Icon className="text-lg" name="arrow-right-from-bracket" />
              Sign out
            </NavLink>
          </Menu.Item>
        </Menu.Nav>
      </Menu>
    </div>
  );
}
